<template>
  <div class="row">
    <form @submit.prevent="onSubmit" class="col-md-12">
      <InstrumentForm :propertyInstrument="propertyInstrument"
        @set-instrument-contact="updateContact"
        :isNewInstrument="true"
        :submitting="submitting">
        <template v-slot:additionalFields :instrument="propertyInstrument.instrument">
          <hr>
          <div class="form-group row">
            <label for="properties" class="col-lg-4 label-text">
              <span class="text-danger">*</span>Associated Properties
            </label>
            <div class="loading col-lg-4" v-if="propertiesLoading">
              Loading...
            </div>
            <div class="col-lg-8">
              <b-form-checkbox-group
                v-if="!propertiesLoading"
                v-model="propertyInstrument.instrument.associatedPropertyIds"
                v-validate="'multi_required'"
                data-vv-as="Associated Property"
                name="properties"
              >
                <b-form-checkbox
                  v-for="property in orderedProjectProperties"
                  :key="property.id"
                  :value="property.id"
                  class="col-md-5 noselect mb-2">
                  {{ getPropertyLabel(property)}}
                </b-form-checkbox>
              </b-form-checkbox-group>
              <div v-if="errors.first('properties')" class="invalid-feedback d-block mb-2">
                {{ errors.first('properties') }}
              </div>
            </div>
          </div>
          <div class="form-group row">
            <label for="additionalProperties" class="col-lg-4 label-text">
              Cross-Project Properties
            </label>
            <div class="col-lg-8">
              <ul v-if="associatedProjects.length !== 0" class="list-group mb-2">
                <li class="list-group-item" v-for="project in associatedProjects" :key="project.id">
                  <div class="d-flex justify-content-between">
                    <h6>Project {{project.id}} - Current Properties</h6>
                    <a class="form-action-text text-secondary p-0"
                      @click='checkAllForProject(project)'>
                      <font-awesome-icon icon='check' />
                      Select All Properties
                    </a>
                    <a class="form-action-icon text-danger p-0"
                      @click='removeProject(project)'>
                      <font-awesome-icon icon='trash-alt' />
                    </a>
                  </div>
                  <b-form-checkbox-group
                    v-model="crossProjectPropertyIds"
                    :key="project.id"
                    :name="`properties-${project.id}`"
                  >
                    <b-form-checkbox
                      v-for="property in project.properties"
                      :key="property.id"
                      :value="property.id"
                      class="col-md-5 noselect mb-2"
                    >
                      {{ getPropertyLabel(property)}}
                    </b-form-checkbox>
                  </b-form-checkbox-group>
                </li>
              </ul>
              <div class="row">
                <GetProperties
                  class="col-md-10"
                  :projects="allProjects"
                  @projectAdded="addProject"
                />
              </div>
            </div>
          </div>
          <StaffLeadAssignment
            :staffLeadSelected="staffLeadSelected"
            :staffLead="propertyInstrument.instrument.staffLead"
            :staffLeadName="propertyInstrument.instrument.staffLeadName"
            :showSystemDefaultWarning="false"
            @update-staff-lead-selected="updateStaffLeadSelected"
          ></StaffLeadAssignment>
          <div class="form-group row">
            <label for="instrumentNotes" class="col-lg-4 label-text">
              Instrument Notes
            </label>
            <div class="col-lg-8">
              <textarea data-vv-as="Notes"
                        type="textarea"
                        class="form-control"
                        name="notes"
                        v-model="propertyInstrument.instrument.notes"
                        placeholder="Enter Notes"
                        rows="3"
                        maxlength="1500" />
                <small v-if="!errors.first('notes')" class="form-text text-muted">
                  Max 1500 characters
                </small>
                <div class="invalid-feedback">{{ errors.first('notes') }}</div>
            </div>
          </div>
        </template>
        <template v-slot:submitForm>
          <div class='card-footer right-align-content'>
            <b-button variant="danger"
              class="button-row-spacing"
              v-b-modal="'cancelNewInstrument'"
              :disabled="submitting">
              Cancel
            </b-button>
            <b-button @click="submitWrapper(propertyInstrument.instrument)"
              :disabled="submitting">
              Save
            </b-button>
          </div>
          <CancelModal
            :modalId="'cancelNewInstrument'"
            @cancelled="cancel">
          </CancelModal>
        </template>
      </InstrumentForm>
    </form>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import FormSubmit from '@/mixins/FormSubmit';
import RouterCheck from '@/mixins/RouterCheck';
import InstrumentForm from '../components/InstrumentForm.vue';
import CancelModal from '../components/CancelModal.vue';
import GetProperties from '../components/GetProperties.vue';
import StaffLeadAssignment from '@/components/common/StaffLeadAssignment.vue';

export default {
  name: 'NewInstrument',
  components: {
    InstrumentForm,
    CancelModal,
    GetProperties,
    StaffLeadAssignment,
  },
  mixins: [FormSubmit, RouterCheck],
  data() {
    return {
      propertyInstrument: {
        instrument: {
          agreementTypeId: '',
          contacts: [],
          associatedPropertyIds: [],
          notes: '',
          staffLead: null,
          staffLeadName: null,
        },
      },
      projectProperties: [],
      associatedProjects: [],
      crossProjectPropertyIds: [],
      staffLeadSelected: null,
    };
  },
  mounted() {
    this.$store.dispatch('Common/startEditMode', 'Instrument');
    this.$store.dispatch('Property/fetchProperties', parseInt(this.$route.params.project_id, 10));
    this.$store.dispatch('User/fetchActiveUsers')
      .then(() => {
        this.staffLeadSelected = this.getCurrentUserFromActiveUsers;
      });
  },
  computed: {
    ...mapState('Instrument', {
      submitting: state => state.submitting,
    }),
    ...mapState('Instrument', {
      propertiesLoading: state => state.propertiesLoading,
    }),
    ...mapGetters('Property', {
      orderedProjectProperties: 'getOrderedCurrentProperties',
    }),
    ...mapState('User', {
      activeUsers: state => state.activeUsers,
      loadingUsers: state => state.loading,
      user: state => state.user,
    }),
    ...mapGetters('User', {
      getCurrentUserFromActiveUsers: 'getCurrentUserFromActiveUsers',
    }),
    allProjects() {
      return this.associatedProjects.concat({ id: parseInt(this.$route.params.project_id, 10) });
    },
  },
  methods: {
    getPropertyLabel(property) {
      if (property.calculatedAcres) {
        return `${property.id} - ${this.$options.filters.acresFormat(property.calculatedAcres)} Acres`;
      }
      return `${property.id} - Unspecified Acreage`;
    },
    submitWrapper(instrument) {
      const entity = {};
      Object.assign(entity, instrument);
      entity.associatedPropertyIds = entity.associatedPropertyIds
        .concat(this.crossProjectPropertyIds);
      entity.projectId = parseInt(this.$route.params.project_id, 10);
      if (this.staffLeadSelected) {
        entity.staffLead = this.staffLeadSelected.id;
        entity.staffLeadName = this.staffLeadSelected.name;
      }
      this.onSubmit(entity, 'Instrument', null, this.routeToNewInstrument);
    },
    routeToNewInstrument(id) {
      this.$store.dispatch('Common/stopEditMode', 'Instrument');
      this.$router.push({
        name: 'projectInstrument',
        params: {
          project_id: parseInt(parseInt(this.$route.params.project_id, 10), 10),
          id,
        },
      });
    },
    cancel() {
      this.$store.dispatch('Common/stopEditMode', 'Instrument');
      this.$router.push({
        name: 'projectInstruments',
        params: { project_id: parseInt(this.$route.params.project_id, 10) },
      });
    },
    updateContact(contact) {
      this.propertyInstrument.instrument.contacts = [contact];
    },
    removeProject(project) {
      this.crossProjectPropertyIds = this.crossProjectPropertyIds
        .filter(propertyId => !project.properties.some(prop => prop.id === propertyId));
      this.associatedProjects.splice(
        this.associatedProjects.findIndex(ap => ap.id === project.id),
        1,
      );
    },
    addProject(project) {
      this.associatedProjects.push(project);
    },
    checkAllForProject(project) {
      const uniqueIds = this.crossProjectPropertyIds;
      uniqueIds.push(...project.properties.map(property => property.id));
      this.crossProjectPropertyIds = [...new Set(uniqueIds)];
    },
    updateStaffLeadSelected(newStaffLead) {
      this.staffLeadSelected = newStaffLead;
    },
  },
};
</script>
